import { VizType } from "@atlas-int/data";
/**
 * Type-safe routing utilities powered by React Router v7 typegen.
 *
 * The types in this module are derived from the route definitions in
 * `app/routes.ts` via `react-router typegen`. Run `pnpm typegen` to
 * regenerate after changing route paths.
 *
 * This module bridges three systems:
 * - React Router route paths (from typegen)
 * - VizType enum (from @atlas-int/data)
 * - Per-viz query param types (from paramSchemas.ts, derived from Zod schemas)
 */

import { useParams, useLocation } from "react-router";
import type {
  TreeParams,
  GeoParams,
  OverTimeParams,
  MarketShareParams,
  FeasibilityParams,
  ProductSpaceParams,
  VizParamMap,
} from "../visualization/paramSchemas";

// Re-export the type-safe href function from react-router.
// With the generated +routes.ts registering Pages, this is fully typed:
//   href("/rankings/:year", { year: "2024" })  ✅
//   href("/rankings/:year", {})                 ❌ TS error: year is required
//   href("/countries/:country?/:slide?", {})    ✅ optional params
export { href } from "react-router";

// ── Route path types from typegen ──────────────────────────────────

/** Access the registered Pages type from react-router's module augmentation */
type RegisteredPages = import("react-router").Register extends {
  pages: infer P;
}
  ? P
  : never;

/** All registered page paths */
export type PagePath = keyof RegisteredPages;

/** Extract the params type for a given page path */
export type PageParams<Path extends PagePath> =
  RegisteredPages[Path]["params"];

// ── Explore route types ────────────────────────────────────────────

/** Route paths for explore visualizations, derived from typegen */
export type ExploreRoutePath =
  | "/explore/treemap"
  | "/explore/geomap"
  | "/explore/overtime"
  | "/explore/marketshare"
  | "/explore/productspace"
  | "/explore/feasibility/*";

/**
 * Maps VizType enum values to their route paths.
 * This is the single source of truth connecting VizType ↔ route path.
 *
 * The `satisfies` clause enforces:
 * 1. Every VizType has a route path entry (Record<VizType, …>)
 * 2. Every path value is a real registered page path (PagePath)
 *
 * If a route is added/renamed in app/routes.ts without updating this map,
 * TypeScript will error here.
 */
export const vizTypeToRoutePath = {
  [VizType.Tree]: "/explore/treemap",
  [VizType.Geo]: "/explore/geomap",
  [VizType.OverTime]: "/explore/overtime",
  [VizType.MarketShare]: "/explore/marketshare",
  [VizType.ProductSpace]: "/explore/productspace",
  [VizType.Feasibility]: "/explore/feasibility",
} as const satisfies Record<VizType, string>;

// Compile-time assertion: every viz route path is either a direct PagePath or
// a prefix of a splat route (e.g., "/explore/feasibility" → "/explore/feasibility/*").
type IsRegisteredExploreRoute<S extends string> =
  S extends PagePath ? true :
  `${S}/*` extends PagePath ? true : never;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _AssertVizRoutesRegistered = {
  [V in VizType]: IsRegisteredExploreRoute<(typeof vizTypeToRoutePath)[V]>;
};

/** Reverse mapping: route path segment → VizType */
const segmentToVizType: Record<string, VizType> = Object.fromEntries(
  Object.entries(vizTypeToRoutePath).map(([vt, path]) => [
    path.split("/")[2],
    vt as VizType,
  ]),
) as Record<string, VizType>;

// ── VizType ↔ route path ↔ query param type mapping ────────────────

/**
 * Maps VizType to its narrowed query param interface.
 *
 * This connects the VizType discriminant to the per-viz param shapes
 * defined in paramSchemas.ts, creating a single type-level lookup:
 *
 *   type Params = VizQueryParams[VizType.Tree]  // → TreeParams
 */
export type VizQueryParams = {
  [VizType.Tree]: TreeParams;
  [VizType.Geo]: GeoParams;
  [VizType.OverTime]: OverTimeParams;
  [VizType.MarketShare]: MarketShareParams;
  [VizType.Feasibility]: FeasibilityParams;
  [VizType.ProductSpace]: ProductSpaceParams;
};

/**
 * Connects route paths to their VizType and query param schema.
 * This creates a single type-level registry:
 *
 *   ExploreRouteConfig["/explore/treemap"]["params"]  // → TreeParams
 *   ExploreRouteConfig["/explore/treemap"]["vizType"]  // → VizType.Tree
 */
export type ExploreRouteConfig = {
  [V in VizType]: {
    path: (typeof vizTypeToRoutePath)[V];
    vizType: V;
    params: VizParamMap[V];
  };
};

// Compile-time assertion: VizQueryParams and VizParamMap must stay in sync.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _AssertParamMapsMatch = VizQueryParams extends VizParamMap ? true : never;

// ── Typed hooks ────────────────────────────────────────────────────

/**
 * Type-safe wrapper around useParams() for a specific route path.
 *
 *   const { country, slide } = typedUseParams("/countries/:country?/:slide?");
 *   const { year } = typedUseParams("/rankings/product/:year?");
 */
export function typedUseParams<Path extends PagePath>(
  _path: Path,
): PageParams<Path> {
  return useParams() as PageParams<Path>;
}

const defaultVizType = VizType.Tree;

/**
 * Extract VizType from the current route pathname.
 *
 * Replaces scattered `pathname.split("/")[2]` calls with a centralized,
 * type-safe extraction. Returns VizType.Tree as default if the segment
 * doesn't match a known viz type.
 */
export function useCurrentVizType(): VizType {
  const location = useLocation();
  const segment = location.pathname.split("/")[2];
  return segmentToVizType[segment] ?? defaultVizType;
}

/**
 * Whether the current explore route is showing the feasibility table sub-view.
 *
 * The feasibility viz has a `/explore/feasibility/table` sub-path that shows
 * a table instead of the scatterplot. This replaces scattered checks like:
 *   location.pathname.includes("feasibility/table")
 *   location.pathname.split("/").slice(-1)[0] === "table"
 *   location.pathname.split("/")[3] === "table"
 */
export function useIsFeasibilityTable(): boolean {
  const location = useLocation();
  const path = location.pathname;
  return (
    (path.startsWith("/explore/feasibility/") ||
      path.startsWith("/explore-m/feasibility/")) &&
    path.endsWith("/table")
  );
}

/**
 * Whether the current explore route is the product space visualization.
 * Replaces: location.pathname.includes("/productspace")
 */
export function useIsProductSpace(): boolean {
  const vizType = useCurrentVizType();
  return vizType === VizType.ProductSpace;
}
